/**
 * Icons.tsx
 *
 * Canonical Orbi control icons as inline SVG, stroked with currentColor so they
 * inherit the token-driven colour of their button (brand and vertical aware, no
 * hardcoded hex). Replaces every emoji glyph in the dock and panel (item 6).
 *
 * One small set, consistent stroke weight, sized to the em box. No emoji.
 */

import React from 'react';

interface IconProps {
  size?: number;
  className?: string;
}

const base = (size: number): React.SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

export const MicIcon: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg {...base(size)} className={className}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="21" />
  </svg>
);

export const PhoneIcon: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg {...base(size)} className={className}>
    <path d="M4 5c0 8.3 6.7 15 15 15a1.6 1.6 0 0 0 1.6-1.6v-2.4a1.3 1.3 0 0 0-1-1.3l-3-.7a1.3 1.3 0 0 0-1.3.4l-1 1a11.5 11.5 0 0 1-5-5l1-1a1.3 1.3 0 0 0 .4-1.3l-.7-3A1.3 1.3 0 0 0 8.9 3H6.6A1.6 1.6 0 0 0 5 4.6Z" />
  </svg>
);

export const CloseIcon: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg {...base(size)} className={className}>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

export const SendIcon: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg {...base(size)} className={className}>
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="6 11 12 5 18 11" />
  </svg>
);

/** Hang-up: phone tilted down. Used to end a live call. */
export const HangupIcon: React.FC<IconProps> = ({ size = 18, className }) => (
  <svg {...base(size)} className={className}>
    <path d="M3 11a13 13 0 0 1 18 0l-2.2 2.2a1.3 1.3 0 0 1-1.6.2l-2.3-1.3a1.3 1.3 0 0 1-.6-1.4l.3-1.3a9.5 9.5 0 0 0-5.8 0l.3 1.3a1.3 1.3 0 0 1-.6 1.4l-2.3 1.3a1.3 1.3 0 0 1-1.6-.2Z" />
  </svg>
);

/** Navigate affordance: arrow leaving to the upper right. */
export const NavigateIcon: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...base(size)} className={className}>
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="8 7 17 7 17 16" />
  </svg>
);

export default { MicIcon, PhoneIcon, CloseIcon, SendIcon, HangupIcon, NavigateIcon };
